import {
  AiRedoIcon,
  DownloadIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Tooltip,
  TrashDeleteIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { TSegment } from '@/features/segments/types';

type TMonitoringContentMiniCardActionsProps = {
  item: TSegment;
  size?: IconButtonSizeEnum;
  className?: string;
  iconButtonColor?: IconButtonColorEnum;
  onReview?: (id: string) => void;
  onDownload?: (id: string) => void;
  onAi?: (id: string) => void;
  onDelete?: (id: string) => void;
};

export function BroadcasterScheduleProgramMiniCardCardActions({
  item,
  className,
  size,
  iconButtonColor = IconButtonColorEnum.NONE_DEFAULT,
  onReview,
  onAi,
  onDelete,
  onDownload,
}: TMonitoringContentMiniCardActionsProps) {
  const { t } = useTranslation();

  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);

  const handleClickReview = (event: { stopPropagation: () => void }) => {
    setIsSelectOpen(!isSelectOpen);
    event.stopPropagation();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        className={clsx('flex items-center p-size-4', className)}
        onClick={event => {
          event.stopPropagation();
        }}
      >
        {onReview && (
          <Tooltip help={t('Revisión')} grow>
            <div ref={selectRef}>
              <IconButton
                className="relative"
                size={size}
                color={iconButtonColor}
                onClick={e => {
                  handleClickReview(e);
                  e.stopPropagation();
                }}
                onMouseDown={e => e.stopPropagation()}
              >
                <>
                  <StatusSeenChipSelect
                    value={item?.review_state || 0}
                    onChange={key => {
                      setIsSelectOpen(false);
                      onReview?.(key);
                    }}
                    listOrientation="left"
                    asIcon
                    onMenuClick={e => e.stopPropagation()}
                    reviewStates={SEGMENT_REVIEW_STATES}
                  />
                </>
              </IconButton>
            </div>
          </Tooltip>
        )}
        {onDownload && (
          <Tooltip help={t('Descargar')} grow>
            <IconButton
              size={size}
              color={iconButtonColor}
              onClick={() => onDownload(item.presigned_url || '')}
            >
              <DownloadIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        )}
        {onAi && (
          <Tooltip help={t('Relanzar')} grow>
            <IconButton size={size} color={iconButtonColor} onClick={() => onAi(item.id)}>
              <AiRedoIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip help={t('Eliminar')} grow>
            <IconButton size={size} color={iconButtonColor} onClick={() => onDelete(item.id)}>
              <TrashDeleteIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </>
  );
}
